/**
 * Slug Generation Utilities
 *
 * Creates human-readable URL slugs from quiz titles and generates short unique IDs.
 */

/**
 * Convert a string to a URL-friendly slug
 * Example: "US Area Codes Quiz!" → "us-area-codes-quiz"
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/[\s_-]+/g, '-') // Replace spaces/underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Generate a short unique ID (8 characters, alphanumeric)
 * Uses crypto.randomUUID() and encodes to base62 for brevity
 */
export function generateShortId(): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const randomBytes = crypto.getRandomValues(new Uint8Array(6));

  let result = '';
  for (let i = 0; i < randomBytes.length; i++) {
    const byte = randomBytes[i];
    if (byte !== undefined) {
      result += chars[byte % chars.length];
    }
  }

  return result;
}

/**
 * Create a full shareable slug from title and short ID
 * Example: "US Area Codes Quiz", "abc123" → "us-area-codes-quiz-abc123"
 */
export function createShareableSlug(title: string, shortId: string): string {
  const slug = slugify(title);
  return `${slug}-${shortId}`;
}

/**
 * Parse a shareable slug back into components
 * Example: "us-area-codes-quiz-abc123" → { slug: "us-area-codes-quiz", shortId: "abc123" }
 * Also handles legacy UUID format for backwards compatibility
 */
export function parseShareableSlug(shareableSlug: string): {
  shortId: string;
  slug: string | null;
} {
  // Check if it's a UUID (legacy format)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(shareableSlug)) {
    return {
      shortId: shareableSlug, // Treat UUID as shortId for lookup
      slug: null,
    };
  }

  // Extract the last segment as shortId (everything after the last hyphen)
  const lastHyphenIndex = shareableSlug.lastIndexOf('-');

  if (lastHyphenIndex === -1) {
    // No hyphen found, treat entire string as shortId
    return {
      shortId: shareableSlug,
      slug: null,
    };
  }

  const shortId = shareableSlug.substring(lastHyphenIndex + 1);
  const slug = shareableSlug.substring(0, lastHyphenIndex);

  return {
    shortId,
    slug: slug || null,
  };
}
